import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, LoadingBlock, formatDateTime } from "@/components/Common";
import { Monitor, CheckCircle2, XCircle } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import api, { formatApiError } from "@/lib/api";

export default function Security() {
  const { lang } = useI18n();
  const L = (it, en, de) => (lang === "en" ? en : lang === "de" ? (de ?? it) : it);
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState(null);

  useEffect(() => { api.get("/auth/login-history").then(({ data }) => setHistory(data)).catch(() => setHistory([])); }, []);

  const changePw = async () => {
    if (f.new_password !== f.confirm) { toast.error(L("Le password non coincidono", "Passwords do not match", "Passwörter stimmen nicht überein")); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      toast.success(L("Password aggiornata", "Password updated", "Passwort aktualisiert"));
      setF({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <h2 className="font-heading text-2xl font-medium text-white">{L("Sicurezza", "Security", "Sicherheit")}</h2>
      <Card className="p-6 space-y-4">
        <h3 className="font-heading text-lg text-white">{L("Cambia password", "Change password", "Passwort ändern")}</h3>
        <div><Label className="text-slate-300">{L("Password attuale", "Current password", "Aktuelles Passwort")}</Label><Input type="password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} className="mt-1.5 bg-navy border-white/10" data-testid="current-password" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-slate-300">{L("Nuova password", "New password", "Neues Passwort")}</Label><Input type="password" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} className="mt-1.5 bg-navy border-white/10" data-testid="new-password" /></div>
          <div><Label className="text-slate-300">{L("Conferma", "Confirm", "Bestätigen")}</Label><Input type="password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} className="mt-1.5 bg-navy border-white/10" data-testid="confirm-password" /></div>
        </div>
        <Button onClick={changePw} disabled={saving} className="bg-gold text-navy hover:bg-gold-hover" data-testid="change-password-btn">{saving ? L("Aggiornamento…", "Updating…", "Wird aktualisiert…") : L("Aggiorna password", "Update password", "Passwort aktualisieren")}</Button>
      </Card>

      <Card className="p-6">
        <h3 className="font-heading text-lg text-white mb-4">{L("Attività di accesso", "Login activity", "Anmeldeaktivität")}</h3>
        {history === null ? <LoadingBlock /> : history.length === 0 ? <p className="text-sm text-slate-500">{L("Nessun accesso registrato.", "No logins recorded.", "Keine Anmeldungen erfasst.")}</p> : (
          <div className="space-y-2" data-testid="login-history">
            {history.map((h) => (
              <div key={h.id} className="flex items-center justify-between rounded-lg border border-white/10 bg-navy px-4 py-3">
                <div className="flex items-center gap-3">
                  <Monitor className="h-4 w-4 text-slate-500" />
                  <div>
                    <p className="text-sm text-slate-300">{h.ip || L("IP sconosciuto", "Unknown IP", "Unbekannte IP")}</p>
                    <p className="text-xs text-slate-600 truncate max-w-xs">{h.user_agent}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  {h.success ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <XCircle className="h-4 w-4 text-red-400" />}
                  <span className="text-slate-500">{formatDateTime(h.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
